import _ from 'underscore';
import $ from 'jquery';
import React from 'react';
import PropTypes from 'prop-types';
import { slugify } from 'underscore.string';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Button } from '@bingads-webui-react/primitive-unthemed';
import { keyCode } from '@bingads-webui/keycode';
import { ContextualMenu } from '@bingads-webui-react/fabric-control';
import { WithManagedFocuser } from '@bingads-webui-react/hoc-with-managed-focuser';
import { noopFactory } from '@bingads-webui-react/hoc-with-expression-editor-controls';

const stopPropagation = e => e.stopPropagation();
const emphasizeMatch = (text, start, end = text.length) => {
  const prefix = text.slice(0, start);
  const emphasis = text.slice(start, end);
  const suffix = text.slice(end, text.length);

  return <span>{prefix}<strong>{emphasis}</strong>{suffix}</span>;
};

const getStyle = () => ({
  inputContainer: {
    position: 'relative',
    whiteSpace: 'pre',
    display: 'inline-block',
    marginLeft: 3,
  },
  suggestionButton: {
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#e1dfdd !important',
      color: '#323130 !important',
    },
    '&:focus': {
      color: '#323130 !important',
    },
    '&.btn:focus': {
      outline: '1px solid #323130',
      outlineOffset: -1,
    },
    '&.btn.focus:after, &.btn:focus:after, &.btn:active:focus': {
      border: 0,
    },
  },
  input: {
    position: 'absolute',
    left: 0,
    right: 0,
    width: '100%',
    padding: 0,
    border: 'none',
    outline: 'none',
  },
});

class UnManagedInputAutocomplete extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: this.props.value,
    };

    this.menuTarget = React.createRef(null);
    this.menuRef = React.createRef(null);
    this.inComposition = false;
  }

  render() {
    const {
      value,
      onDropdownValueSelected,
      suggestions,
      i18n,
      isFocused,
      showSuggestions,
      onFocus,
      focusableRef,
      onClick,
      onChange,
      onKeyUp,
      zIndexStart,
      autoCompleteDropDownClassName,
      classes,
    } = this.props;
    const id = `input-autocomplete-${slugify(value)}`;
    const cleanInputValue = value.trim();
    const valueLower = cleanInputValue.toLocaleLowerCase();
    const suggestedMetrics = _.flatten(_.map(suggestions, ({ metrics }) =>
      _.map(metrics, ({ metric, titleKey }) => {
        const title = i18n.getString(titleKey);
        const titleLower = title.toLocaleLowerCase();
        const matchStart = titleLower.indexOf(valueLower);
        const visible = matchStart >= 0;
        const renderTitle = visible
          ? emphasizeMatch(title, matchStart, matchStart + valueLower.length)
          : title;

        return {
          title,
          exprValue: metric,
          visible,
          renderTitle,
          matchStart,
        };
      }))).sort((metric1, metric2) => {
      if (metric1.matchStart !== metric2.matchStart) {
        return metric1.matchStart - metric2.matchStart;
      }

      return metric1.title.length - metric2.title.length;
    });
    const show =
     isFocused
     && showSuggestions
      && !_.isEmpty(cleanInputValue)
      && !_.isEmpty(_.where(suggestedMetrics, { visible: true }));

    const menuItems = _.chain(suggestedMetrics).filter(suggestion => suggestion.visible).map(({
      title,
      exprValue,
      renderTitle,
    }, index) => ({
      key: `${index}-${exprValue}`,
      text: title,
      renderTitle,
      exprValue,
      onClick: () => {
        onDropdownValueSelected({ value: exprValue, title });
      },
      onRender: item => (
        <Button
          onClick={() => {
            if (item.onClick) {
              onDropdownValueSelected({ value: exprValue, title });
            }
          }}
          key={item.text}
          role="menuitem"
          className={classes.suggestionButton}
          btnStyle="link"
        >
          {renderTitle}
        </Button>
      ),
    })).value();

    const onKeyDown = (event) => {
      if (show) {
        if (event.keyCode === keyCode.ArrowDown) {
          $(`.${autoCompleteDropDownClassName} button`)[0].focus();
        } else if (event.keyCode === keyCode.Enter) {
          onDropdownValueSelected({ value: menuItems[0].exprValue, title: menuItems[0].text });
        }
      }
    };

    if (_.size(menuItems) === 1) {
      const menuItem = _.first(menuItems);

      if (menuItem.text.toLocaleLowerCase() === valueLower) {
        _.defer(() => {
          onDropdownValueSelected({ value: menuItem.exprValue, title: menuItem.text });
        });
      }
    }

    return (
      <div
        className="dropdown"
        role="presentation"
        onFocus={onFocus}
        onClick={stopPropagation}
      >
        <span
          ref={this.menuTarget}
          className={classes.inputContainer}
        >
          {`${this.state.inputValue} `}
          <input
            id={id}
            ref={focusableRef}
            value={this.state.inputValue}
            className={classes.input}
            onClick={onClick}
            onCompositionStart={() => {
              this.inComposition = true;
            }}
            onCompositionEnd={(e) => {
              this.inComposition = false;
              onChange(e);
              onKeyDown(e);
              this.setState({ inputValue: e.target.value });
            }}
            onChange={(e) => {
              if (!this.inComposition) {
              onChange(e);
              }
              this.setState({ inputValue: e.target.value });
            }}
            onKeyUp={(e) => {
              if (!this.inComposition) {
                onKeyUp(e);
              }
            }}
            onKeyDown={(e) => {
              if (!this.inComposition) {
                onKeyDown(e);
              }
            }}
            style={{
              zIndex: zIndexStart + 5,
          }}
          />
        </span>
        {show ?
          <ContextualMenu
            componentRef={this.menuRef}
            target={this.menuTarget}
            hidden={!show}
            items={menuItems}
            shouldFocusOnMount={false}
            className={autoCompleteDropDownClassName}
          /> : null}
      </div>
    );
  }
}

UnManagedInputAutocomplete.propTypes = {
  autoCompleteDropDownClassName: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  value: PropTypes.string,
  showSuggestions: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    titleKey: PropTypes.string.isRequired,
    metrics: PropTypes.arrayOf(PropTypes.shape({
      metric: PropTypes.string.isRequired,
      titleKey: PropTypes.string.isRequired,
    })).isRequired,
  })),
  isFocused: PropTypes.bool,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  onKeyUp: PropTypes.func,
  onDropdownValueSelected: PropTypes.func,
  focusableRef: PropTypes.func,
  zIndexStart: PropTypes.number,
  classes: PropTypes.shape(_.mapObject(
    getStyle({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

UnManagedInputAutocomplete.defaultProps = {
  value: '',
  suggestions: [],
  isFocused: false,
  showSuggestions: false,
  onFocus: _.noop,
  onClick: _.noop,
  onChange: _.noop,
  onKeyUp: _.noop,
  onDropdownValueSelected: noopFactory,
  focusableRef: _.noop,
  zIndexStart: 10000,
};

export const InputAutocomplete =
  WithManagedFocuser(withDefaultStyles(UnManagedInputAutocomplete, getStyle));
